/**
 * Photo capture upload helper used by PhotosPanel.
 *
 * Wraps the offline-aware `uploadFileOffline` with two extras that the
 * field flow needs:
 *   1. A short-lived geolocation lookup with a hard wall-clock timeout —
 *      iOS sometimes ignores the navigator option and would otherwise
 *      block the upload indefinitely behind a permission prompt.
 *   2. An optional "save to phone" pass that opens the iOS/Android
 *      share sheet BEFORE we await any network, preserving the user
 *      gesture so the share sheet is actually allowed to show.
 *
 * Extracted from PhotosPanel.jsx to keep that file render-only.
 */
import { uploadFileOffline } from "@/lib/uploadOffline";
import { addUpload, updateUpload } from "@/lib/uploadStatus";
import { isVideoFile, uploadVideoOffline } from "@/lib/videoUpload";

// Tag each captured file with rough GPS for later mapping/audit. The
// short timeout matters because the upload runs inline with the user's
// "Add Photo" tap and we don't want to block them on a slow GPS lock.
async function _resolveGps() {
  return Promise.race([
    new Promise((resolve) => {
      if (typeof navigator === "undefined" || !navigator.geolocation) return resolve({});
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => resolve({}),
        { timeout: 2000, maximumAge: 600000 }
      );
    }),
    new Promise((resolve) => setTimeout(() => resolve({}), 2500)),
  ]);
}

// Web can't write to the Camera Roll directly. The Web Share API on
// iOS/Android opens a sheet that includes "Save Image", which is the
// closest one-tap workflow we can get. Must be called synchronously
// inside the gesture, BEFORE any await.
//
// Filename convention: <label>_<timestamp>.<ext> — no site number
// because customers/foremen forwarding photos out of the phone
// didn't want internal site IDs bleeding into the filename.
export async function offerSaveToPhone(file, label /* , _siteNumberDeprecated */) {
  if (typeof navigator === "undefined" || !navigator.canShare || !navigator.share) return;
  try {
    const ext = (file.name?.split(".").pop() || "jpg").toLowerCase();
    const safeLabel = (label || "photo").replace(/\s+/g, "_");
    const renamed = new File(
      [file],
      `${safeLabel}_${Date.now()}.${ext}`,
      { type: file.type || "image/jpeg" }
    );
    if (!navigator.canShare({ files: [renamed] })) return;
    await navigator.share({ files: [renamed], title: label });
  } catch {
    // User dismissed the share sheet or it's unsupported. Either way,
    // the upload itself proceeds untouched.
  }
}

/**
 * Upload a batch of captured files for one requirement slot. Returns
 * `{ uploaded, queued }` counts so the caller can pick the right toast
 * (online success / offline queued / mixed).
 */
export async function uploadCapturedPhotos({ projectId, files, reqInfo }) {
  const ids = files.map((f) => addUpload(f.name || "photo", reqInfo.label));
  const gps = await _resolveGps();
  let uploaded = 0;
  let queued = 0;
  let failed = 0;
  let firstError = null;
  for (let i = 0; i < files.length; i += 1) {
    try {
      const fields = {
        kind: "photo",
        category: reqInfo.category,
        requirement_key: reqInfo.key,
        ...(gps.lat != null ? { gps_lat: gps.lat } : {}),
        ...(gps.lng != null ? { gps_lng: gps.lng } : {}),
      };
      const id = ids[i];
      // eslint-disable-next-line no-await-in-loop
      const r = isVideoFile(files[i])
        ? await uploadVideoOffline({
          projectId, file: files[i], fields, label: reqInfo.label,
          onProgress: (pct) => updateUpload(id, { message: `Uploading video ${pct}%` }),
        })
        : await uploadFileOffline({ projectId, file: files[i], label: reqInfo.label, fields });
      if (r.queued) {
        queued += 1;
        updateUpload(ids[i], { status: "waiting", queueId: r.queueId, message: "Saved on this device — will retry automatically" });
      } else {
        uploaded += 1;
        updateUpload(ids[i], { status: "done" });
      }
    } catch (err) {
      failed += 1;
      const msg = err?.response?.data?.detail || err?.message || "Upload failed";
      firstError = firstError || msg;
      updateUpload(ids[i], { status: "failed", message: msg });
    }
  }
  return { uploaded, queued, failed, firstError };
}
